import React, { LegacyRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SimpleBar from 'simplebar-react';
import { Button, OffcanvasBody } from 'react-bootstrap';

// redux
import { AppDispatch, RootState } from '../../redux/store';
import { changeLayoutPosition, changeLayoutTheme, changeLayoutType, changeLayoutWidth, changeSideBarTheme, changeSideBarType, changeTopBarTheme } from '../../redux/actions';

// constants
import * as layoutConstants from '../../constants/layout';

// components
import LayoutTheme from './LayoutTheme';
import LayoutType from './LayoutType';
import LayoutWidth from './LayoutWidth';
import TopBarTheme from './TopBarTheme';
import SideBarTheme from './SideBarTheme';
import SideBarType from './SideBarType';
import LayoutPosition from './LayoutPosition';

interface ThemeCustomizerProps {
  handleRightSideBar: (value: any) => void;
  rightBarNodeRef: LegacyRef<HTMLDivElement> | undefined;
}

const ThemeCustomizer = ({
  handleRightSideBar,
  rightBarNodeRef,
}: ThemeCustomizerProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    layoutType,
    layoutTheme,
    layoutWidth,
    topBarTheme,
    sideBarTheme,
    sideBarType,
    layoutPosition,
  } = useSelector((state: RootState) => ({
    layoutType: state.Layout.layoutType,
    layoutTheme: state.Layout.layoutTheme,
    layoutWidth: state.Layout.layoutWidth,
    topBarTheme: state.Layout.topBarTheme,
    sideBarTheme: state.Layout.sideBarTheme,
    sideBarType: state.Layout.sideBarType,
    layoutPosition: state.Layout.layoutPosition,
  }));

  /**
   * Changes the layout type
   */
  const handleChangeLayoutType = (type: string) => {
    switch (type) {
      case 'horizontal':
        dispatch(changeLayoutType(layoutConstants.LayoutType.LAYOUT_HORIZONTAL));
        break;
      default:
        dispatch(changeLayoutType(layoutConstants.LayoutType.LAYOUT_VERTICAL));
        break;
    }
  }

  /**
   * Changes the layout theme
   */
  const handleChangeLayoutTheme = (theme: string) => {
    switch (theme) {
      case 'dark':
        dispatch(changeLayoutTheme(layoutConstants.LayoutTheme.THEME_DARK));
        break;
      default: dispatch(changeLayoutTheme(layoutConstants.LayoutTheme.THEME_LIGHT));
        break;
    }
  }

  /**
   * Changes the layout width
   */
  const handleChangeLayoutWidth = (layoutWidth: string) => {
    switch (layoutWidth) {
      case 'boxed':
        dispatch(changeLayoutWidth(layoutConstants.LayoutWidth.LAYOUT_WIDTH_BOXED));
        break;
      case 'detached':
        dispatch(changeLayoutWidth(layoutConstants.LayoutWidth.LAYOUT_WIDTH_DETACHED));
        break;
      default:
        dispatch(changeLayoutWidth(layoutConstants.LayoutWidth.LAYOUT_WIDTH_FLUID));
        break;
    }
  }

  /**
   * Changes the topbar theme
   */
  const handleChangeTopBarTheme = (theme: string) => {
    switch (theme) {
      case 'dark':
        dispatch(changeTopBarTheme(layoutConstants.TopBarTheme.TOPBAR_DARK));
        break;
      case 'brand':
        dispatch(changeTopBarTheme(layoutConstants.TopBarTheme.TOPBAR_BRAND));
        break;
      default:
        dispatch(changeTopBarTheme(layoutConstants.TopBarTheme.TOPBAR_LIGHT));
        break;
    }
  }

  /**
   * Changes the left sidebar theme
   */
  const handleChangeSideBarTheme = (theme: string) => {
    switch (theme) {
      case 'dark':
        dispatch(changeSideBarTheme(layoutConstants.SideBarTheme.LEFT_SIDEBAR_THEME_DARK));
        break;
      case 'brand':
        dispatch(changeSideBarTheme(layoutConstants.SideBarTheme.LEFT_SIDEBAR_THEME_BRAND));
        break;
      case 'gradient':
        dispatch(changeSideBarTheme(layoutConstants.SideBarTheme.LEFT_SIDEBAR_THEME_GRADIENT));
        break;
      default:
        dispatch(changeSideBarTheme(layoutConstants.SideBarTheme.LEFT_SIDEBAR_THEME_LIGHT));
        break;
    }
  };

  /**
   * Changes the left sidebar type
   */
  const handleChangeSideBarType = (type: string) => {
    switch (type) {
      case 'compact':
        dispatch(changeSideBarType(layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_COMPACT));
        break;
      case 'condensed':
        dispatch(changeSideBarType(layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_CONDENSED));
        break;
      case 'full':
        dispatch(changeSideBarType(layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_FULL));
        break;
      case 'fullscreen':
        dispatch(changeSideBarType(layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_FULLSCREEN));
        break;
      default:
        dispatch(changeSideBarType(layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_DEFAULT));
        break;
    }
  };

  /**
   * Changes the layout position
   */
  const handleChangeLayoutPosition = (position: string) => {
    switch (position) {
      case 'scrollable':
        dispatch(changeLayoutPosition(layoutConstants.LayoutPosition.POSITION_SCROLLABLE));
        break;
      default:
        dispatch(changeLayoutPosition(layoutConstants.LayoutPosition.POSITION_FIXED));
        break;
    }
  };

  /**
   * Reset Layout
   */
  const reset = () => {
    handleChangeLayoutType(layoutConstants.LayoutType.LAYOUT_VERTICAL);
    handleChangeLayoutTheme(layoutConstants.LayoutTheme.THEME_LIGHT);
    handleChangeLayoutWidth(layoutConstants.LayoutWidth.LAYOUT_WIDTH_FLUID);
    handleChangeTopBarTheme(layoutConstants.TopBarTheme.TOPBAR_BRAND);
    handleChangeSideBarTheme(layoutConstants.SideBarTheme.LEFT_SIDEBAR_THEME_LIGHT);
    handleChangeSideBarType(layoutConstants.SideBarType.LEFT_SIDEBAR_TYPE_DEFAULT);
    handleChangeLayoutPosition(layoutConstants.LayoutPosition.POSITION_FIXED);
  };

  return (
    <React.Fragment>
      <div
        className='fw-semibold px-3 m-0 py-2 font-16 bg-light d-flex align-items-center'
        ref={rightBarNodeRef}
      >
        <span className='d-block py-2'>Theme Settings</span>
        <button
          type='button'
          className='btn-close ms-auto'
          onClick={handleRightSideBar}
        />
      </div>
      <OffcanvasBody>
        <SimpleBar scrollbarMaxSize={320} className='h-100'>
          <div className='alert alert-warning' role='alert'>
            <strong>Customize </strong> the overall color scheme, sidebar menu, etc.
          </div>

          {/* layout type */}
          <LayoutType
            handleChangeLayoutType={handleChangeLayoutType}
            layoutType={layoutType}
            layoutConstants={layoutConstants.LayoutType}
          />

          {/* layout theme */}
          <LayoutTheme
            handleChangeLayoutTheme={handleChangeLayoutTheme}
            layoutTheme={layoutTheme}
            layoutConstants={layoutConstants.LayoutTheme}
          />

          {/* layout width */}
          <LayoutWidth
            handleChangeLayoutWidth={handleChangeLayoutWidth}
            layoutWidth={layoutWidth}
            layoutConstants={layoutConstants.LayoutWidth}
          />

          {/* top bar theme */}
          <TopBarTheme
            handleChangeTopBarTheme={handleChangeTopBarTheme}
            topBarTheme={topBarTheme}
            layoutConstants={layoutConstants.TopBarTheme}
          />

          {/* side bar theme */}
          <SideBarTheme
            handleChangeSideBarTheme={handleChangeSideBarTheme}
            sideBarTheme={sideBarTheme}
            layoutConstants={layoutConstants.SideBarTheme}
          />

          {/* layout position */}
          <LayoutPosition
            handleChangeLayoutPosition={handleChangeLayoutPosition}
            layoutPosition={layoutPosition}
            layoutConstants={layoutConstants.LayoutPosition}
          />

          {/* side bar type */}
          <SideBarType
            handleChangeSideBarType={handleChangeSideBarType}
            sideBarType={sideBarType}
            layoutConstants={layoutConstants.SideBarType}
          />

        </SimpleBar>
      </OffcanvasBody>
      <div className='offcanvas-footer border-top px-3 py-2 text-center'>
        <div className='d-flex gap-2'>
          <Button
            variant='light'
            className='w-50'
            onClick={reset}
          >
            Reset
          </Button>
          <a href='https://wrapbootstrap.com/theme/codefox-admin-dashboard-template-WB0X27670?ref=coderthemes' target='_blank' rel='noreferrer' className='btn btn-danger w-50'>
            <i className='mdi mdi-basket me-1' />
            Buy
          </a>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ThemeCustomizer;
